/* ============================================================
   Photozyme — cart drawer subscription control (Recharge)

   The cart drawer is Rebuy Smart Cart. Rebuy's own subscription
   controls are built from the legacy SKIO selling-plan group and
   know nothing about Recharge, so on a Recharge line they
   (a) show the wrong frequency — Skio's first option — and
   (b) rewrite the line to a SKIO selling plan when used, silently
   moving the customer off Recharge. Rebuy renders two of them:

     subscription line -> select.rebuy-select        (frequency)
     one-time line     -> button.rebuy-button        ("Upgrade to
                                                      Subscription")

   Both are replaced here by equivalents built from the product's
   Recharge plans (selling-plan app_id 294517), following Rebuy's
   own two-state logic exactly — their markup carries one control
   per state, never both:

     one-time line     -> "Upgrade to Subscription & Save N%"
     subscription line -> frequency select (its "One-time only"
                          option downgrades, which brings the
                          button back)

   Ours carry Rebuy's own `rebuy-button` / `rebuy-select` classes
   and their optgroup structure, so they are visually identical to
   the controls they replace.

   Only products on the subscription allow-list get a control at all
   (window.PZ_SUB_HANDLES, rendered from
   snippets/pz-subscription-allowlist.liquid). Everything else shows
   no subscription UI in the drawer, since Rebuy's own controls are
   hidden by the stylesheet regardless.

   No flash of the Skio control: the CSS hides Rebuy's originals as
   soon as `pz-cart-sub-armed` is on <html>, which is set below at
   parse time — long before the drawer is ever opened — and the
   cart plus product data are prefetched so ours paints with the
   first render rather than a frame later.

   No document-wide MutationObserver: Rebuy re-renders its whole
   flyout on every cart change, so we re-sync from a cheap interval
   that exits immediately when the drawer is closed.

   Build 5 adds a one-shot cart migration for carts that predate the
   Recharge switch. Shopify carts persist for weeks (and the cookie
   renews on every visit), so a returning customer's saved cart can
   still hold lines attached to a SKIO selling plan — those price and
   check out fine, and would create a subscription in Skio, an app
   that is being decommissioned, invisible to the Recharge migration.
   Incognito looks right only because incognito starts a fresh cart.

   On page load, before the drawer renders, each such line is
   rewritten to the Recharge plan with the same cadence (Skio
   "Every 6 weeks" -> Recharge "6 week subscription with 30%
   discount" — same price, same rhythm, so the shopper sees no
   change), falling back to the shortest offered plan, or to one-time
   when the product is no longer on the subscription allow-list.
   Once the SKIO selling plans are deleted from the products in the
   admin, this pass simply never finds anything to do.
   ============================================================ */
(function () {
  "use strict";

  /* Armed so the stylesheet can hide Rebuy's Skio controls from the very
     first paint of the drawer. The snippet already does this inline, before
     the stylesheet; this is the belt-and-braces copy for when the asset is
     loaded on its own. Guarded because at document-start — an init script,
     an early injection — documentElement can still be null. */
  if (document.documentElement) {
    document.documentElement.classList.add("pz-cart-sub-armed");
  }

  window.__pzCartSubBuild = 5;

  var RECHARGE_APP_ID = "294517";

  /* Only these products may sell subscriptions. The list is rendered into
     window.PZ_SUB_HANDLES by snippets/pz-cart-subscription.liquid, which
     reads snippets/pz-subscription-allowlist.liquid — the same single source
     the PDP buy box uses, so a product the buy box refuses to sell as a
     subscription can't be upgraded to one from the cart either.

     If the global is missing the snippet was not uploaded with this asset.
     Fall back to allowing every product — i.e. previous behaviour, still
     Recharge-only, never Skio — rather than silently killing subscription
     management everywhere, and say so in the console. */
  function subscribable(handle) {
    var list = window.PZ_SUB_HANDLES;
    if (!list || !list.length) {
      if (!subscribable._warned) {
        subscribable._warned = true;
        console.warn(
          "[pz-cart-subscription] window.PZ_SUB_HANDLES is missing — upload " +
            "snippets/pz-subscription-allowlist.liquid and pz-cart-subscription.liquid. " +
            "Falling back to allowing every product."
        );
      }
      return true;
    }
    return list.indexOf(handle) !== -1;
  }

  /* Same allow-list as the PDP buy box (snippets/pz-buybox-recharge.liquid):
     show 4/6/8-week plans, and fall back to every plan on products that
     only carry monthly ones, so the dropdown is never empty. */
  var PLAN_ALLOW = ["4-week", "6-week", "8-week"];

  var ROOT_SEL = "#rebuy-cart";
  var ITEM_SEL = ".rebuy-cart__flyout-item";
  var SUB_SEL = ".rebuy-cart__flyout-item-subscription";
  var INFO_SEL = ".rebuy-cart__flyout-item-info";

  /* Rebuy's defaults, used when the app's own language settings
     aren't readable for some reason. */
  var FALLBACK = {
    onetime_option_group_label: "Pay Full Price",
    onetime_option_label: "One-time only",
    subscription_option_group_label: "Subscribe and Save {{ subscription_discount }}%",
    upgrade_to_subscription_label: "Upgrade to Subscription & Save {{ subscription_discount }}%",
    upgrade_to_subscription_without_discount_label: "Upgrade to Subscription",
    upgrading_label: "Upgrading...",
    downgrading_label: "Downgrading...",
    upgrade_to_subscription_default_frequency: ""
  };

  var POLL_MS = 200;
  var CART_TTL_MS = 1500;

  var productCache = {}; // handle -> Promise<{plans, prices}>
  var cartCache = null;
  var cartCachedAt = 0;
  var busy = false; // set while we mutate, so our own edits don't re-trigger

  /* ---------- Rebuy's own copy ---------- */

  function rebuyLanguage() {
    try {
      var sub = window.Rebuy.SmartCart.settings.switch_to_subscription;
      return sub && sub.language ? sub.language : FALLBACK;
    } catch (e) {
      return FALLBACK;
    }
  }

  function fillDiscount(template, pct) {
    return String(template || "").replace(/\{\{\s*subscription_discount\s*\}\}/g, pct);
  }

  /* ---------- data ---------- */

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch("/products/" + handle + ".js", {
        headers: { Accept: "application/json" }
      })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(parseProduct)
        .catch(function () {
          return null;
        });
    }
    return productCache[handle];
  }

  /* Collect the Recharge plans, plus a variant -> plan -> price index, from
     the /products/<handle>.js payload. Its shape differs from the Liquid
     object: allocations carry selling_plan_id, not a nested selling_plan. */
  function parseProduct(data) {
    if (!data || !data.selling_plan_groups) return null;

    var plans = [];
    var legacy = {}; // planId -> days between orders (null when unparsable)
    data.selling_plan_groups.forEach(function (group) {
      if (String(group.app_id) !== RECHARGE_APP_ID) {
        /* Any other app's plans (in practice: SKIO) are what the sanitizer
           migrates away from. Cadence comes from names like "Every 6 weeks". */
        (group.selling_plans || []).forEach(function (plan) {
          legacy[String(plan.id)] = nameToDays(plan.name);
        });
        return;
      }
      (group.selling_plans || []).forEach(function (plan) {
        var freq = "";
        (plan.options || []).forEach(function (opt) {
          if (opt.name === "Order Frequency and Unit") {
            freq = String(opt.value || "").toLowerCase().trim();
          }
        });
        if (!freq) return;
        plans.push({ id: String(plan.id), freq: freq, days: freqToDays(freq), label: freqToLabel(freq) });
      });
    });
    /* Unlike earlier builds, a product with legacy plans but no Recharge
       plans still returns (with plans: []) so the sanitizer can see the
       legacy ids. Render paths check plans.length before using it. */
    if (!plans.length && !Object.keys(legacy).length) return null;

    plans.sort(function (a, b) {
      return a.days - b.days;
    });

    var kept = plans.filter(function (p) {
      return PLAN_ALLOW.indexOf(p.freq) !== -1;
    });
    if (!kept.length) kept = plans;

    var prices = {}; // variantId -> { price, plans: { planId: price } }
    (data.variants || []).forEach(function (v) {
      var byPlan = {};
      (v.selling_plan_allocations || []).forEach(function (a) {
        byPlan[String(a.selling_plan_id)] = a.price;
      });
      prices[String(v.id)] = { price: v.price, plans: byPlan };
    });

    return { plans: kept, prices: prices, legacy: legacy };
  }

  /* Cadence from a legacy plan's display name — Skio's read "Every 4 weeks",
     "Every 6 weeks", "Every 8 weeks". Null when nothing parses. */
  function nameToDays(name) {
    var m = String(name || "").match(/(\d+)\s*(day|week|month|year)/i);
    if (!m) return null;
    return freqToDays(m[1] + "-" + m[2].toLowerCase());
  }

  function freqToDays(freq) {
    var parts = freq.split("-");
    var n = parseInt(parts[0], 10) || 1;
    switch (parts[1]) {
      case "day":
        return n;
      case "week":
        return n * 7;
      case "month":
        return n * 30;
      case "year":
        return n * 365;
      default:
        return n * 30;
    }
  }

  /* "6-month" -> "Every 6 months", matching how Rebuy's originals read. */
  function freqToLabel(freq) {
    var parts = freq.split("-");
    var n = parseInt(parts[0], 10) || 1;
    var unit = parts[1] || "month";
    return n > 1 ? "Every " + n + " " + unit + "s" : "Every " + unit;
  }

  function getCart(force) {
    var now = Date.now();
    if (!force && cartCache && now - cartCachedAt < CART_TTL_MS) {
      return Promise.resolve(cartCache);
    }
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        cartCache = cart;
        cartCachedAt = Date.now();
        return cart;
      })
      .catch(function () {
        return cartCache;
      });
  }

  /* Pull the cart and every product in it up front, so the control is ready
     to draw the moment the drawer opens instead of a network round trip
     later — that gap is what shows as a flicker. */
  function prewarm() {
    return getCart(true).then(function (cart) {
      if (!cart) return;
      var seen = {};
      (cart.items || []).forEach(function (item) {
        if (item.handle && !seen[item.handle] && subscribable(item.handle)) {
          seen[item.handle] = true;
          getProduct(item.handle);
        }
      });
    });
  }

  /* ---------- matching a drawer row to a cart line ---------- */

  function handleOf(row) {
    var match = String(row.className || "").match(/(?:^|\s)product-([\w-]+)/);
    return match ? match[1] : null;
  }

  /* Rebuy can hide lines (bundle children, free gifts), so a plain index
     lookup across the whole cart is not safe. Pair within a handle instead:
     the nth drawer row for a handle is the nth cart line for that handle. */
  function pairRows(rows, cart) {
    var seen = {};
    return rows.map(function (row) {
      var handle = handleOf(row);
      if (!handle) return null;
      var n = seen[handle] || 0;
      seen[handle] = n + 1;
      var matches = (cart.items || []).filter(function (i) {
        return i.handle === handle;
      });
      var item = matches[n];
      return item ? { row: row, item: item, handle: handle } : null;
    });
  }

  /* ---------- rendering ---------- */

  function currentPlanId(item) {
    var alloc = item.selling_plan_allocation;
    return alloc && alloc.selling_plan ? String(alloc.selling_plan.id) : "";
  }

  function bestDiscount(product, item) {
    var variant = product.prices[String(item.variant_id)];
    if (!variant || !variant.price) return 0;
    var best = 0;
    product.plans.forEach(function (plan) {
      var planPrice = variant.plans[plan.id];
      if (planPrice == null) return;
      var pct = Math.round(((variant.price - planPrice) * 100) / variant.price);
      if (pct > best) best = pct;
    });
    return best;
  }

  /* Which plan an "Upgrade to Subscription" click should land on: Rebuy's own
     configured default frequency when it is set and still offered, otherwise
     the shortest interval — the same default the PDP buy box starts on. */
  function defaultPlan(product) {
    var wanted = String(rebuyLanguage().upgrade_to_subscription_default_frequency || "").toLowerCase().trim();
    if (wanted) {
      for (var i = 0; i < product.plans.length; i++) {
        if (product.plans[i].freq === wanted) return product.plans[i];
      }
    }
    return product.plans[0];
  }

  /* Rebuy's markup exactly, and the same one-control-per-state logic:
     a one-time line gets the upgrade button, a subscription line gets the
     frequency select. Never both — Rebuy renders the other as an empty
     placeholder comment. */
  function buildControl(pair, product) {
    return currentPlanId(pair.item) ? buildSelect(pair, product) : buildUpgradeButton(pair, product);
  }

  function buildUpgradeButton(pair, product) {
    var lang = rebuyLanguage();
    var pct = bestDiscount(product, pair.item);
    var plan = defaultPlan(product);

    var button = document.createElement("button");
    button.type = "button";
    button.className = "rebuy-button outline pz-cart-sub__upgrade";
    button.setAttribute("data-pz-cart-sub", "");
    button.setAttribute("data-pz-sig", "onetime");
    button.setAttribute(
      "aria-label",
      "Switch " + (pair.item.product_title || pair.item.title || "item") + " to a Subscription"
    );

    var span = document.createElement("span");
    span.textContent = pct > 0
      ? fillDiscount(lang.upgrade_to_subscription_label || FALLBACK.upgrade_to_subscription_label, pct)
      : lang.upgrade_to_subscription_without_discount_label ||
        FALLBACK.upgrade_to_subscription_without_discount_label;
    button.appendChild(span);

    button.addEventListener("click", function () {
      if (!plan) return;
      span.textContent = lang.upgrading_label || FALLBACK.upgrading_label;
      changePlan(pair.item, plan.id, button);
    });
    return button;
  }

  function buildSelect(pair, product) {
    var lang = rebuyLanguage();
    var pct = bestDiscount(product, pair.item);
    var current = currentPlanId(pair.item);

    var select = document.createElement("select");
    select.className = "rebuy-select muted pz-cart-sub__select";
    select.setAttribute("aria-label", "Subscription delivery frequency");
    select.setAttribute("data-pz-cart-sub", "");

    var oneGroup = document.createElement("optgroup");
    oneGroup.label = fillDiscount(lang.onetime_option_group_label || FALLBACK.onetime_option_group_label, pct);
    var one = document.createElement("option");
    one.value = "";
    one.textContent = lang.onetime_option_label || FALLBACK.onetime_option_label;
    oneGroup.appendChild(one);
    select.appendChild(oneGroup);

    var subGroup = document.createElement("optgroup");
    subGroup.label = fillDiscount(
      lang.subscription_option_group_label || FALLBACK.subscription_option_group_label,
      pct
    );
    product.plans.forEach(function (plan) {
      var opt = document.createElement("option");
      opt.value = plan.id;
      opt.textContent = plan.label;
      subGroup.appendChild(opt);
    });

    /* A line can sit on a Recharge plan the allow-list hides (or, on the
       published theme, on an old Skio plan). Add it so the control can never
       misrepresent what the shopper is actually buying. */
    if (current && !subGroup.querySelector('option[value="' + current + '"]')) {
      var extra = document.createElement("option");
      extra.value = current;
      var alloc = pair.item.selling_plan_allocation;
      extra.textContent = (alloc && alloc.selling_plan && alloc.selling_plan.name) || "Current plan";
      subGroup.appendChild(extra);
    }
    select.appendChild(subGroup);

    select.value = current;
    select.setAttribute("data-pz-sig", "sub:" + current + ":" + select.options.length);
    select.addEventListener("change", function () {
      /* Picking "One-time only" is Rebuy's downgrade path; the button comes
         back on the next render, exactly as it does in their own control. */
      if (!select.value) {
        var opt = select.options[select.selectedIndex];
        if (opt) opt.textContent = lang.downgrading_label || FALLBACK.downgrading_label;
      }
      changePlan(pair.item, select.value, select);
    });
    return select;
  }

  function changePlan(item, planId, control) {
    control.disabled = true;
    busy = true;
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        selling_plan: planId || ""
      })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        cartCache = cart;
        cartCachedAt = Date.now();
        /* Hand the fresh cart straight to Rebuy so prices and the
           free-shipping bar update without a second round trip. */
        try {
          if (window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.setCart) {
            window.Rebuy.SmartCart.setCart(cart);
          }
        } catch (e) {}
        document.dispatchEvent(new CustomEvent("pz:cart-subscription-changed", { detail: { cart: cart } }));
      })
      .catch(function () {})
      .then(function () {
        control.disabled = false;
        busy = false;
        sync(true);
      });
  }

  /* ---------- one-shot Skio cart migration (build 5) ---------- */

  /* Rewrite every cart line still attached to a legacy (Skio) selling plan.
     Target: the Recharge plan with the same cadence — same 30% price, same
     delivery rhythm, so nothing visibly changes for the shopper — falling
     back to the shortest offered plan, or to one-time when the product is
     no longer allowed to sell subscriptions. Lines whose product data can't
     be read are left untouched rather than guessed at.

     Changes run sequentially: concurrent /cart/change.js calls race on the
     cart and the later response silently wins. */
  function migrateLegacyLines(cart) {
    if (!cart || !cart.items || !cart.items.length) return Promise.resolve(cart);

    var jobs = cart.items.map(function (item) {
      var planId = currentPlanId(item);
      if (!planId || !item.handle) return Promise.resolve(null);
      return getProduct(item.handle).then(function (product) {
        if (!product || !(planId in product.legacy)) return null; // Recharge or unknowable — leave alone
        var target = "";
        if (subscribable(item.handle) && product.plans.length) {
          var days = product.legacy[planId];
          var match = null;
          product.plans.forEach(function (p) {
            if (days && p.days === days) match = p;
          });
          target = (match || product.plans[0]).id;
        }
        return { key: item.key, quantity: item.quantity, selling_plan: target };
      });
    });

    return Promise.all(jobs).then(function (changes) {
      changes = changes.filter(Boolean);
      if (!changes.length) return cart;
      busy = true;
      var chain = Promise.resolve(cart);
      changes.forEach(function (change) {
        chain = chain.then(function (latest) {
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({
              id: change.key,
              quantity: change.quantity,
              selling_plan: change.selling_plan
            })
          })
            .then(function (r) {
              return r.ok ? r.json() : latest;
            })
            .catch(function () {
              return latest;
            });
        });
      });
      return chain.then(function (finalCart) {
        busy = false;
        cartCache = finalCart;
        cartCachedAt = Date.now();
        try {
          if (window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.setCart) {
            window.Rebuy.SmartCart.setCart(finalCart);
          }
        } catch (e) {}
        document.dispatchEvent(
          new CustomEvent("pz:cart-migrated", { detail: { cart: finalCart, changed: changes.length } })
        );
        return finalCart;
      });
    });
  }

  function sanitizeCart() {
    if (sanitizeCart._done) return Promise.resolve(cartCache);
    sanitizeCart._done = true;
    return getCart(true).then(function (cart) {
      if (!cart) return null;
      return migrateLegacyLines(cart);
    });
  }

  /* ---------- sync loop ---------- */

  function sync(force) {
    if (busy) return;
    var root = document.querySelector(ROOT_SEL);
    if (!root) return;

    var rows = Array.prototype.slice.call(root.querySelectorAll(ITEM_SEL));
    if (!rows.length) return;

    /* Cheap exit: every row is settled — it either carries a control, or is
       a product that may not sell subscriptions and has been marked as such.
       Rebuy re-creates these nodes on each render, so both marks are
       re-applied rather than assumed to persist. */
    if (!force) {
      var missing = rows.some(function (row) {
        return !row.querySelector("[data-pz-cart-sub]") && !row.hasAttribute("data-pz-no-sub");
      });
      if (!missing) return;
    }

    getCart(force).then(function (cart) {
      if (!cart) return;
      pairRows(rows, cart).forEach(function (pair) {
        if (!pair) return;
        /* Not on the allow-list: render nothing. Rebuy's own controls stay
           hidden by the stylesheet, so the line simply has no subscription
           UI — which is the point. */
        if (!subscribable(pair.handle)) {
          pair.row.setAttribute("data-pz-no-sub", "");
          return;
        }
        getProduct(pair.handle).then(function (product) {
          if (product && product.plans.length) renderRow(pair, product);
        });
      });
    });
  }

  function renderRow(pair, product) {
    var row = pair.row;
    if (!row.isConnected) return;

    var host = row.querySelector(SUB_SEL);
    if (!host) {
      /* Rebuy only renders that container while its own switcher is enabled.
         With the setting off we still want a control, so make one — in the
         same position Rebuy uses, a direct child of the line after the info
         column, so it inherits the same layout. */
      host = document.createElement("div");
      host.className = "rebuy-cart__flyout-item-subscription";
      var info = row.querySelector(INFO_SEL);
      if (info && info.parentElement === row) {
        row.insertBefore(host, info.nextSibling);
      } else {
        row.appendChild(host);
      }
    }

    var existing = host.querySelector("[data-pz-cart-sub]");
    var control = buildControl(pair, product);

    busy = true;
    try {
      if (existing) {
        /* data-pz-sig covers both states, so a button and a select are
           comparable: same signature means nothing has changed. */
        if (existing.getAttribute("data-pz-sig") === control.getAttribute("data-pz-sig")) {
          return; /* already correct — leave the live node alone */
        }
        host.replaceChild(control, existing);
      } else {
        host.appendChild(control);
      }
    } finally {
      busy = false;
    }
  }

  function start() {
    prewarm()
      .then(sanitizeCart)
      .then(function () {
        sync(true);
      });
    setInterval(sync, POLL_MS);
    ["rebuy:cart.show", "rebuy:cart.ready", "rebuy:cart.change"].forEach(function (evt) {
      document.addEventListener(evt, function () {
        sync(true);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
